import { getTheme, IColumn } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPShimmeredDetailsList, IPText } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import GenericModal from 'components/Dialogs/GenericModal';
import HrefLink from 'components/Links/HrefLink';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import { globalStackTokensGapExtraSmall } from 'globalStyles';
import { IdentityProviderType } from 'models/identityProvider';
import Task from 'models/tasks/task';
import { WebhookRun } from 'models/webhook';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWebhookRunsForTask } from 'services/Api/webhookService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateTimeMedium } from 'utils/datetime';
import { sortOnDate } from 'utils/sorting';

interface IEventTaskWorkflowDetailsModalProps {
  hidden: boolean;
  task: Task;
  onClose: () => void;
}

const TaskWorkflowDetailsModal = (props: IEventTaskWorkflowDetailsModalProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'translation']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [runs, setRuns] = useState<WebhookRun[]>([]);
  const theme = getTheme();
  //Power Automate environments are Default-<Entra tenant id>: there is no run page to link for another directory
  const hasPowerAutomate = appContext.user.tenant.identityProviderType === IdentityProviderType.Entra;

  useEffect(() => {
    if (!props.hidden) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.hidden]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const runs = await apiGetWebhookRunsForTask(props.task.taskId, accessToken);
      runs.sort((a, b) => sortOnDate(a.created, b.created));
      setRuns(runs);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: IColumn[] = [
    {
      key: 'created',
      name: t('task:Workflows.Dialogs.EventWorkflowDetails.Columns.Created'),
      minWidth: 120,
      maxWidth: 200,
      isResizable: true,
      onRender: (item?: WebhookRun, index?: number, column?: IColumn) => {
        if (!item) return;

        return <IPText>{toLocaleDateTimeMedium(item.created)}</IPText>;
      },
    },
    {
      key: 'workflow',
      name: t('task:Workflows.Dialogs.EventWorkflowDetails.Columns.Workflow'),
      isMultiline: true,
      minWidth: 150,
      isResizable: true,
      onRender: (item?: WebhookRun, index?: number, column?: IColumn) => {
        if (!item) return;
        if (hasPowerAutomate && item.workflowId && item.workflowRunId) {
          return (
            <IPStack
              horizontal
              tokens={globalStackTokensGapExtraSmall}
              verticalAlign="center"
              styles={{ root: { paddingTop: 5 } }}
            >
              <IPFontIcon iconName="OpenInNewTab" style={{ color: theme.palette.themePrimary }} />
              <IPText>
                <HrefLink href={getWorkflowDetailsUrl(item)} external newTab>
                  {item?.webhook?.workflowName || item.webhook?.eventType || t('task:Workflows.Dialogs.EventWorkflowDetails.Title')}
                </HrefLink>
              </IPText>
            </IPStack>
          );
        } else {
          return <IPText>{item?.webhook?.workflowName}</IPText>;
        }
      },
    },
  ];

  const getWorkflowDetailsUrl = (item: WebhookRun) => {
    if (hasPowerAutomate && item.workflowId && item.workflowRunId) {
      const url = `https://make.powerautomate.com/environments/Default-${appContext.user.tenant.providerTenantId}/flows/${item.workflowId}/runs/${item.workflowRunId}`;

      return url;
    }
  };

  //
  // Main render
  //
  return (
    <GenericModal
      title={t('task:Workflows.Dialogs.EventWorkflowDetails.Title')}
      subTitle={t('task:Workflows.Dialogs.EventWorkflowDetails.SubTitle')}
      isOpen={!props.hidden}
      onClose={props.onClose}
      height={'50vh'}
      minHeight={300}
      maxHeight={'90vh'}
      width={'40vw'}
      minWidth={250}
      maxWidth={'95vw'}
    >
      <IPStack verticalFill>
        <ScrollableStackItem>
          <IPShimmeredDetailsList
            shimmerLines={3}
            enableShimmer={isLoading}
            compact
            items={runs}
            columns={columns}
            selectionMode={IPSelectionMode.none}
            layoutMode={IPDetailsListLayoutMode.justified}
            onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
            selectionPreservedOnEmptyClick={true}
          />
        </ScrollableStackItem>
      </IPStack>
    </GenericModal>
  );
};

export default TaskWorkflowDetailsModal;
